import { FC, useState, useEffect } from 'react';
import { usePrevious } from 'dibs-react-hooks/exports/usePrevious';

import Dot, { dotSizes, DotSizesType } from './components/Dot';

import styles from './PhotoDotsIndicator.scss';

const CENTER_DOTS_COUNT = 3;

type Props = {
    totalItems: number;
    currentIndex: number;
};

const PhotoDotsIndicator: FC<Props> = ({ totalItems, currentIndex }) => {
    const [currentCentralDotIndex, setCurrentCentralDotIndex] = useState(0);
    const [dotOffset, setDotOffset] = useState(0);
    const prevIndex = usePrevious(currentIndex);

    useEffect(() => {
        if (totalItems <= 1) {
            return;
        }
        const lastIndex = totalItems - 1;
        const isSwipeBetweenLastFirstIndex = prevIndex === lastIndex && currentIndex === 0;
        const isSwipeBetweenFirstLastIndex = currentIndex === lastIndex && prevIndex === 0;
        const isLastIndexes = currentIndex + CENTER_DOTS_COUNT - totalItems >= 0;
        const isPageLoadedDirectly = prevIndex === -1 || prevIndex === undefined;
        const centralDotIndex = currentIndex - dotOffset;

        if (isSwipeBetweenLastFirstIndex) {
            //reset state when swiping from last to first
            setDotOffset(0);
            setCurrentCentralDotIndex(0);
        } else if (isSwipeBetweenFirstLastIndex || (isPageLoadedDirectly && isLastIndexes)) {
            //handling swipe from first to last and loading last indexes directly
            setDotOffset(totalItems - CENTER_DOTS_COUNT);
            setCurrentCentralDotIndex(CENTER_DOTS_COUNT - 1);
        } else if (centralDotIndex >= 0 && centralDotIndex < CENTER_DOTS_COUNT) {
            setCurrentCentralDotIndex(centralDotIndex);
        } else {
            setDotOffset(currentIndex - currentCentralDotIndex);
        }
    }, [currentIndex, currentCentralDotIndex, dotOffset, totalItems, prevIndex]);

    const dots = Array.from(Array(totalItems)).map((i, dotIndex) => {
        let dotSize: DotSizesType;
        const allLargeDots = totalItems <= 5;
        const distanceFromCenterDot = Math.abs(dotIndex - (1 + dotOffset));

        if (distanceFromCenterDot === 0 || distanceFromCenterDot === 1 || allLargeDots) {
            dotSize = dotSizes.LARGE;
        } else if (distanceFromCenterDot === 2) {
            dotSize = dotSizes.MEDIUM;
        } else if (distanceFromCenterDot === 3) {
            dotSize = dotSizes.SMALL;
        } else {
            return null; //don't render Dot
        }

        const isActive = currentIndex === dotIndex;
        return <Dot key={dotIndex} isActive={isActive} size={dotSize} />;
    });

    if (totalItems <= 1) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>{dots}</div>
        </div>
    );
};

export default PhotoDotsIndicator;
